import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Icon } from "@/components/ui/Icon";
import { showToast } from "@/lib/toast";
import { formatClockTime, isoWeekNumber } from "@/lib/format";
import { workspaceColor } from "@/lib/colors";
import { t } from "@/lib/strings";
import { useDeleteMemo } from "@/hooks/useMemo";
import type { MemoMutations } from "@/hooks/useMemo";
import type { MeetingSeries, Memo, MemoUser } from "@/lib/types";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { FinalizeMemoDialog } from "@/components/dialogs/FinalizeMemoDialog";
import { MemoAuditDrawer } from "@/components/dialogs/MemoAuditDrawer";
import { MemoParticipantsDialog } from "@/components/dialogs/MemoParticipantsDialog";
import { ProtokolleHeader, ghostButtonStyle } from "@/components/memo/ui/ProtokolleHeader";
import { StatusBadge } from "@/components/memo/ui/StatusBadge";
import { MetaChip } from "@/components/memo/ui/MetaChip";
import { CountPill } from "@/components/memo/ui/CountPill";
import { MemoOpenTasks } from "@/components/memo/MemoOpenTasks";
import { MemoDateEditor } from "@/components/memo/MemoDateEditor";
import { openTasksOf } from "@/lib/memoOpenTasks";

interface Props {
  memo: Memo;
  series: MeetingSeries | undefined;
  mut: MemoMutations;
  /** Scroll to + flash an entry in the document below (and, while presenting, spotlight its
   *  Zwischenabschnitt). Owned by MemoPage, which holds the scroll container and the
   *  presentation cursor. */
  onJumpToEntry: (entryId: string) => void;
  /** Every face this Protokoll may need to draw — the SAME union that feeds the `@`-mention
   *  picker (`lib/memoPeople`). 🔴 Passed in rather than derived from `memo.participants`: a
   *  to-do can belong to someone who missed the meeting (holiday), and resolving assignees
   *  from the attendance roster drew them as no avatar at all. */
  people: Map<string, MemoUser>;
  /** "Nur Neues" view filter (owned by MemoPage). The toggle only renders when
   *  `newCount > 0` — it appears exactly when there is something new to glance at. */
  newOnly: boolean;
  onToggleNewOnly: () => void;
  newCount: number;
}

/** Wrapper that composes the shared `ProtokolleHeader` "1A header" skeleton
 * for the Protokoll-Detail (MemoPage). Owns all memo-header state (the
 * finalize/audit/participants dialogs) and handlers — only the visual shell
 * moved to the shared primitives, nothing about editing behavior. */
export function MemoHeader({
  memo,
  series,
  mut,
  onJumpToEntry,
  people,
  newOnly,
  onToggleNewOnly,
  newCount,
}: Props) {
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [partsOpen, setPartsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  const finalized = memo.status === "finalized";
  // The "Bearbeiter für finalisierte Protokolle" right — the FE mirror of
  // memo_access.require_trusted_edit. NOT `canEdit`: on a draft that means every participant.
  const trusted = memo.access.can_edit_finalized || memo.access.can_admin;
  const navigate = useNavigate();
  const deleteMemo = useDeleteMemo(memo.id, memo.series_id);

  const parts = memo.participants.length;
  const partsSuffix = t.seriesParticipantsLabel.split(" ")[1] ?? "Teilnehmer";
  const partsLabel = `${parts} ${partsSuffix}`;

  // 🔴 ONE definition of "open to-do", shared with the panel below (lib/memoOpenTasks). The
  // pill counting one set while the list shows another is worse than no pill at all.
  const openTasks = openTasksOf(memo);
  const openTaskCount = openTasks.length;
  const openTasksLabel =
    openTaskCount === 1 ? "1 offene Aufgabe" : `${openTaskCount} offene Aufgaben`;

  const kickerColor = series ? workspaceColor(series.home_workspace_id) : "var(--ink-4)";
  const kickerLabel = series ? `${series.name} · Protokoll` : "Protokoll";

  const participantsChip = <MetaChip icon="person">{partsLabel}</MetaChip>;

  return (
    <div style={{ marginTop: 10 }}>
      <ProtokolleHeader
        kicker={{ color: kickerColor, label: kickerLabel }}
        title={<MemoDateEditor memo={memo} mut={mut} />}
        badge={<StatusBadge status={finalized ? "finalized" : "draft"} />}
        actions={
          <>
            {/* 🔴 NOT `canEdit`. On a DRAFT canEdit means every participant — and this EMAILS
                the whole roster. A broadcast is not an edit. Mirrors require_trusted_edit. */}
            {trusted && !finalized ? (
              memo.prep_requested_at ? (
                // Already sent for this draft — shown to EVERYONE with the right so
                // nobody re-mails the roster. Not a button; the timestamp is on hover.
                <span
                  style={{ ...ghostButtonStyle, cursor: "default", color: "var(--ink-3)" }}
                  title={new Date(memo.prep_requested_at).toLocaleString("de-DE")}
                >
                  <Icon name="check" size={14} color="var(--ink-3)" />
                  {memo.prep_requested_by
                    ? t.memoPrepAlreadyRequestedBy.replace("{name}", memo.prep_requested_by)
                    : t.memoPrepAlreadyRequested}
                </span>
              ) : (
                <button
                  type="button"
                  style={ghostButtonStyle}
                  onClick={() =>
                    mut.requestPrep.mutate(undefined, {
                      onSuccess: () => showToast(t.memoPrepRequested, "success"),
                    })
                  }
                >
                  <Icon name="bell" size={14} color="var(--ink-2)" />
                  {t.memoRequestPrep}
                </button>
              )
            ) : null}
            {/* Finalisieren / wieder aufnehmen / löschen / Änderungsprotokoll are all the
                "Bearbeiter für finalisierte Protokolle" right now — one gate, mirroring
                require_trusted_edit on the server. The Änderungsprotokoll used to render for
                EVERY viewer; hiding it here is only half the fix, the endpoint is gated too. */}
            {trusted ? (
              <button type="button" style={ghostButtonStyle} onClick={() => setFinalizeOpen(true)}>
                <Icon name={finalized ? "refresh" : "check"} size={14} color="var(--ink-2)" />
                {finalized ? t.memoReopen : t.memoFinalize}
              </button>
            ) : null}
            {trusted ? (
              <button type="button" style={ghostButtonStyle} onClick={() => setAuditOpen(true)}>
                <Icon name="history" size={14} color="var(--ink-2)" />
                {t.memoAuditTitle}
              </button>
            ) : null}
            {trusted ? (
              <button type="button" style={ghostButtonStyle} onClick={() => setDeleteOpen(true)}>
                <Icon name="trash" size={14} color="var(--danger)" />
                {t.memoDeleteMemo}
              </button>
            ) : null}
            {/* Print is a READ action — offered to everyone who can open the Protokoll, not
                gated behind trusted/admin like the editing actions above. */}
            <button
              type="button"
              style={ghostButtonStyle}
              onClick={() => {
                void navigate({ to: "/memo/$memoId/print", params: { memoId: memo.id } });
              }}
            >
              <Icon name="printer" size={14} color="var(--ink-2)" />
              {t.printAction}
            </button>
            {/* "Nur Neues" — a read filter offered to everyone (like Drucken), only
                when there is at least one new point since the last Protokoll. */}
            {newCount > 0 ? (
              <button
                type="button"
                aria-pressed={newOnly}
                onClick={onToggleNewOnly}
                style={
                  newOnly
                    ? {
                        ...ghostButtonStyle,
                        border: "1px solid var(--accent)",
                        background: "var(--accent-soft)",
                        color: "var(--accent)",
                      }
                    : ghostButtonStyle
                }
              >
                <Icon name="star" size={14} color={newOnly ? "var(--accent)" : "var(--ink-2)"} />
                {t.memoNewOnly}
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 17,
                    height: 17,
                    padding: "0 5px",
                    boxSizing: "border-box",
                    borderRadius: 999,
                    background: newOnly ? "var(--accent)" : "var(--hairline-strong)",
                    color: newOnly ? "var(--surface)" : "var(--ink-2)",
                    fontSize: 10.5,
                    fontWeight: 700,
                  }}
                >
                  {newCount}
                </span>
              </button>
            ) : null}
          </>
        }
        chips={
          <>
            {memo.start_time ? (
              <MetaChip icon="date">{formatClockTime(memo.start_time)} Uhr</MetaChip>
            ) : null}
            <MetaChip icon="date">KW {isoWeekNumber(memo.meeting_date)}</MetaChip>
            {/* 🔴 Clickable for EVERYONE, not just admins. It used to render as a bare,
                inert chip for a participant — still reading "10 Teilnehmer", so it looked
                actionable and did nothing. A participant has every right to see who else
                was in their own meeting; the dialog opens read-only for them (canEdit) and
                editable for a TRUSTED editor (series editor ∪ admin). The backend guard on
                PATCH /memos/{id}/participants (now require_trusted_edit) remains the real
                boundary. Trusted editors keep the chip at 0 participants (their way in to
                add some); for everyone else an empty roster has nothing to show. */}
            {trusted || parts > 0 ? (
              <button
                type="button"
                onClick={() => setPartsOpen(true)}
                style={{ all: "unset", cursor: "pointer" }}
              >
                {participantsChip}
              </button>
            ) : null}
            {/* 🔴 A TOGGLE, not a link. It used to be decoration — a count you could not act
                on. It must NOT navigate to the SeriesPage Aufgaben tab either: that lists the
                whole SERIES, while this counts THIS memo, so clicking "13" would land on a
                list of forty. And in presentation it would tear down the screen-share. It
                expands the to-dos in place instead. "alles erledigt" has nothing to open. */}
            {openTaskCount > 0 ? (
              <button
                type="button"
                onClick={() => setTasksOpen((o) => !o)}
                aria-expanded={tasksOpen}
                style={{ all: "unset", cursor: "pointer" }}
              >
                <CountPill tone="open">{openTasksLabel}</CountPill>
              </button>
            ) : (
              <CountPill tone="done">alles erledigt</CountPill>
            )}
          </>
        }
      />

      {tasksOpen ? (
        <MemoOpenTasks
          tasks={openTasks}
          people={people}
          onJump={(entryId) => {
            // Collapse on the way out: the panel is a way IN to the document, and leaving it
            // open would sit between the reader and the line they just asked to be taken to —
            // in presentation, on a shared screen, pushing it off the top of the fold.
            setTasksOpen(false);
            onJumpToEntry(entryId);
          }}
        />
      ) : null}

      <FinalizeMemoDialog
        open={finalizeOpen}
        onOpenChange={setFinalizeOpen}
        memo={memo}
        mut={mut}
      />
      <MemoAuditDrawer open={auditOpen} onOpenChange={setAuditOpen} memoId={memo.id} />
      <MemoParticipantsDialog
        open={partsOpen}
        onOpenChange={setPartsOpen}
        memo={memo}
        mut={mut}
        canEdit={trusted}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t.memoDeleteMemoTitle}
        message={t.memoDeleteMemoMessage}
        confirmLabel={t.memoDeleteMemoConfirm}
        tone="danger"
        busy={deleteMemo.isPending}
        onConfirm={() => {
          deleteMemo.mutate(undefined, {
            onSuccess: () => {
              setDeleteOpen(false);
              showToast(t.memoDeletedToast, "success");
              void navigate({
                to: "/meetings/$seriesId",
                params: { seriesId: memo.series_id },
              });
            },
          });
        }}
      />
    </div>
  );
}
